import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { EmailAutomations } from '@frontend/applications/AffiliatesApp/components/EmailAutomations/EmailAutomations';
import { TooltipProvider } from '@frontend/shadcn/components/ui/tooltip';
import { Button } from '@/shadcn/components/ui/button';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/shadcn/components/ui/tabs';
import {
  Card, CardDescription, CardFooter, CardHeader, CardTitle,
} from '@/shadcn/components/ui/card';

import { H3 } from '@/shadcn/components/typography/h3';
import { H4 } from '@/shadcn/components/typography/h4';
import { P } from '@/shadcn/components/typography/p';
import { Badge } from '@/shadcn/components/ui/badge';
import { OrderingDirection } from '@/app/types/globalTypes';
import {
  TAutomation,
  useListAutomationsQuery,
} from '@/app/hooks/automations';

import { useGetAllWorkletsForParentSpecQuery, useGetProgramById } from '@/app/hooks';
import TemplateModal from '../templateModal/TemplateModal';
import { AutomationBuilderV2 } from '@/app/components/automationV2';
import { WorkletSpecUri } from '@/app/containers/Projects/constants';
import { AutomationExecutionTable } from '../FlexAutomationPage/components/AutomationExecutionTable';

type TProps = {
  projectId: number;
};

const AutomationPageLayout = ({ children }) => (
  <div className="flex flex-col">
    <div className="flex-1  space-y-4 p-8 pt-6">{children}</div>
  </div>
);

const BadgeOn = () => <Badge>On</Badge>;

const BadgeOff = () => <Badge variant="outline">Off</Badge>;

const useAutomationsList = ({ projectId }: { projectId: number }) => {
  const {
    data: { listAutomations: { results: automations } } = {
      listAutomations: {
        results: [] as TAutomation[],
      },
    },
    loading: isLoadingAutomations,
  } = useListAutomationsQuery({
    variables: {
      paging: {
        offset: 0,
        limit: 10,
      },
      filters: {
        program: {
          id: projectId,
        },
      },
      ordering: {
        name: OrderingDirection.ASC,
      },
    },
    fetchPolicy: 'network-only',
  });
  return { automations, isLoadingAutomations };
};

export const AutomationPage = (props: TProps) => {
  const location = useLocation();
  const [isTemplateModalVisible, setIsTemplateModalVisible] = useState(false);
  const [isAutomationV2Visible, setIsAutomationV2Visible] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const { automationV2: isAutomationV2Enabled } = useClientFeatures();

  const openTemplateModal = () => {
    setIsTemplateModalVisible(true);
  };

  const closeTemplateModal = () => {
    setIsTemplateModalVisible(false);
  };

  const openAutomationV2 = () => {
    setIsAutomationV2Visible(true);
  };

  const closeAutomationV2 = () => {
    setIsAutomationV2Visible(false);
    setSelectedTemplateId(null);
  };

  const handleAddAutomationClick = () => {
    if (isAutomationV2Enabled) {
      openAutomationV2();
    } else {
      openTemplateModal();
    }
  };
  const routeMatchHistory = location.pathname.includes('history');
  const tab = routeMatchHistory ? 'history' : 'automations';
  const { projectId } = props;
  const automationsRoute = `/projects/${projectId}/settings/automation`;
  const historyRoute = `/projects/${projectId}/settings/automation/history`;

  const { automations } = useAutomationsList({ projectId });

  // Fetch program data to check for product catalog stage
  const program = useGetProgramById({
    variables: {
      id: projectId,
      getWorkItems: true,
    }
  });
  const {
    data: {
      worklets: specWorklets = undefined,
    } = {},
  } = useGetAllWorkletsForParentSpecQuery({
    variables: {
      specKey: program.data?.program?.specKey,
    },
    skip: !program.data?.program?.specKey,
  });

  const doesProductCatalogStageExists = React.useMemo(() => {
    if (!specWorklets) return false;
    return specWorklets.some(
      (worklet: any) => worklet.specURI === WorkletSpecUri.SendBrandProductCatalogWorkletSpecification
    );
  }, [specWorklets]);

  const isProductCatalogActionDisabled = React.useMemo(() => {
    return !doesProductCatalogStageExists;
  }, [doesProductCatalogStageExists]);

  return (
    <AutomationPageLayout>
      <div className="flex items-center justify-between">
        <H3 className="text-foreground">Automations</H3>
        <div className="flex gap-2">
          <Button onClick={handleAddAutomationClick} variant="default">
            Add Automation
          </Button>
          {/* <Button onClick={openAutomationV2} variant="outline">
            New Automation
          </Button> */}
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <P>Automate project tasks to easily scale your campaigns. Add a new automation or configure an existing one.</P>
        <Tabs value={tab} defaultValue="automations">
          <TabsList>
            <TabsTrigger asChild value="automations">
              <Link className="text-muted-foreground hover:text-foreground" to={automationsRoute}>
                All Automations
              </Link>
            </TabsTrigger>
            <TabsTrigger asChild value="history">
              <Link className="text-muted-foreground hover:text-foreground" to={historyRoute}>
                Run History
              </Link>
            </TabsTrigger>
          </TabsList>
          <TabsContent value="automations">
            {!isAutomationV2Enabled ? (
              <div className="grid sm:grid-cols-3 gap-4 mt-4">
                {automations.map((automation) => (
                  <Card key={automation.id}>
                    <CardHeader>
                      <CardTitle>
                        <div>
                          <H4 className="text-foreground">{automation.name}</H4>
                          <div>{automation.status === 'ACTIVE' ? <BadgeOn /> : <BadgeOff />}</div>
                        </div>
                      </CardTitle>
                      <CardDescription>{automation.description}</CardDescription>
                    </CardHeader>
                    <CardFooter>
                      <div className="flex gap-2">
                        <Button className="pl-0" variant="link" asChild>
                          <Link to={isAutomationV2Enabled
                            ? `/projects/${projectId}/automation_v2/automation/${automation.id}`
                            : `/projects/${projectId}/automation_detail/automation/${automation.id}`}
                          >
                            View
                          </Link>
                        </Button>
                      </div>
                    </CardFooter>
                  </Card>
                ))}
              </div>) :
              (
                <TooltipProvider>
                  <EmailAutomations
                    offerId={projectId.toString()}
                    baseUri="/"
                    isProjectFlow
                    isProductCatalogActionDisabled={isProductCatalogActionDisabled}
                  />
                </TooltipProvider>
              )}
          </TabsContent>
          <TabsContent value="history">
            <AutomationExecutionTable projectId={projectId} />
          </TabsContent>
        </Tabs>
        {/* Template Modal for V1 */}
        <TemplateModal projectId={projectId} isVisible={isTemplateModalVisible} onCancel={closeTemplateModal} />

        {/* AutomationV2 Modal/Full Screen */}
        {isAutomationV2Visible && (
          <AutomationBuilderV2
            projectId={projectId}
            templateId={selectedTemplateId}
            onSave={() => {
              closeAutomationV2();
            }}
            onCancel={closeAutomationV2}
          />
        )}

      </div>
    </AutomationPageLayout>
  );
};
